import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { cssVars } from '../../shared/lib/styledTheme';

export const SidebarContainer = styled.aside`
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: ${cssVars.sidebarWidth};
  background-color: ${cssVars.bgSecondary};
  border-right: 1px solid ${cssVars.borderColor};
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 8px 0;
  z-index: 1000;
  transition: width 0.3s ease;
  overflow: visible;
`;

export const SidebarIconsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  flex: 1;
  overflow-y: auto;
`;

export const SidebarWikiLink = styled(Link)`
  width: 100%;
  text-decoration: none;
  color: inherit;
`;

interface SidebarIconLinkProps {
  $active?: boolean;
}

export const SidebarIconLink = styled(Link)<SidebarIconLinkProps>`
  width: 100%;
  text-decoration: none;
  color: inherit;
  display: block;
  background: transparent;
  border-radius: 12px;
  margin: 0 8px;
  position: relative;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%) ${(props: SidebarIconLinkProps) => props.$active ? 'scaleY(1)' : 'scaleY(0)'};
    height: 60%;
    width: 3px;
    background: linear-gradient(180deg, #0d6efd 0%, #0a58ca 100%);
    border-radius: 0 3px 3px 0;
    opacity: ${(props: SidebarIconLinkProps) => props.$active ? '1' : '0'};
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  }

  &:hover {
    &::before {
      opacity: 1;
      transform: translateY(-50%) scaleY(1);
    }
  }
`;

interface SidebarToggleButtonProps {
  $expanded?: boolean;
}

export const SidebarToggleButton = styled.button<SidebarToggleButtonProps>`
  width: 100%;
  padding: ${(props: SidebarToggleButtonProps) => props.$expanded ? '10px 16px' : '10px 0'};
  margin-bottom: 8px;
  background: none;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: ${(props: SidebarToggleButtonProps) => props.$expanded ? 'flex-start' : 'center'};
  color: ${cssVars.textPrimary};
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border-radius: 12px;
  margin: ${(props: SidebarToggleButtonProps) => props.$expanded ? '2px 8px 8px 8px' : '4px 0 8px 0'};

  &:hover {
    background: linear-gradient(90deg, rgba(13, 110, 253, 0.08) 0%, rgba(13, 110, 253, 0.04) 100%);
    transform: translateX(${(props: SidebarToggleButtonProps) => props.$expanded ? '2px' : '0'});
    color: #0d6efd;
  }

  &:active {
    transform: translateX(${(props: SidebarToggleButtonProps) => props.$expanded ? '2px' : '0'}) scale(0.98);
  }
`;

export const SidebarToggleIcon = styled.span`
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  flex-shrink: 0;
`;

export const SidebarToggleLabel = styled.span`
  margin-left: 12px;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  letter-spacing: -0.01em;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

export const SidebarProfileContainer = styled.div`
  margin-top: auto;
  padding: 16px 0;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

export const BlueHeaderSection = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 125px;
  z-index: -1;
  overflow: visible;
  background-color: #0d6efd;
`;

export const WaveContainer = styled.div`
  position: absolute;
  bottom: -60px;
  left: 0;
  width: 100%;
  overflow: hidden;
  line-height: 0;
  pointer-events: none;
`;

export const WaveSVG = styled.svg`
  width: 100%;
  height: 100px;
`;

export const TopIconsContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 32px;
  gap: 24px;
  width: 100%;
`;

interface MiddleIconsContainerProps {
  $expanded?: boolean;
}

export const MiddleIconsContainer = styled.div<MiddleIconsContainerProps>`
  display: flex;
  flex-direction: column;
  align-items: ${(props: MiddleIconsContainerProps) => props.$expanded ? 'stretch' : 'center'};
  width: 100%;
  gap: 2px;
  margin-top: 112px;
  padding: ${(props: MiddleIconsContainerProps) => props.$expanded ? '0 8px' : '0'};
`;

interface CRMLogoBadgeProps {
  $expanded?: boolean;
}

export const CRMLogoBadge = styled.div<CRMLogoBadgeProps>`
  width: ${(props: CRMLogoBadgeProps) => props.$expanded ? '60px' : '48px'};
  height: ${(props: CRMLogoBadgeProps) => props.$expanded ? '60px' : '48px'};
  border-radius: ${(props: CRMLogoBadgeProps) => props.$expanded ? '16px' : '16px'};
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05);
  cursor: pointer;
  transition: all 0.3s;
  background-color: white;

  &:hover {
    transform: scale(1.1);
  }
`;

interface CRMLogoTextProps {
  $expanded?: boolean;
}

export const CRMLogoText = styled.span<CRMLogoTextProps>`
  color: #0d6efd;
  font-weight: 900;
  font-size: ${(props: CRMLogoTextProps) => props.$expanded ? '20px' : '12px'};
  letter-spacing: -0.05em;
  transition: all 0.3s;

  ${CRMLogoBadge}:hover & {
    transform: scale(1.1);
  }
`;
